import { lazy, Suspense, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Users,
    BookType,
    Radio,
    Bot,
    BarChart3,
    FileText,
    FileStack,
    FlaskConical,
    ToggleLeft,
} from 'lucide-react';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import LoadingRing from '../ui-components/LoadingRing';
import ManageActorsTab from './ManageActorsTab';
import ManageSynonyms from './ManageSynonyms';
import PublicMRIMachines from './PublicMRIMachines';

const AIAdminPage = lazy(
    () => import('../ai-admin/AIAdminPage')
);
const AdminMetricsPage = lazy(
    () => import('../metrics/AdminMetricsPage')
);
const ManageDocumentSchemasPage = lazy(() =>
    import('../reports').then((m) => ({
        default: m.ManageDocumentSchemasPage,
    }))
);
const FeatureFlagsTab = lazy(() => import('./FeatureFlagsTab'));
const PolicyTemplateListPage = lazy(
    () => import('../policy-templates/PolicyTemplateListPage')
);
const ReportingWorkspacePage = lazy(() =>
    import('../reports').then((m) => ({
        default: m.CaseWorkspacePage,
    }))
);

interface TabDef {
    value: string;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
}

const Loading = () => (
    <div className="flex justify-center py-16">
        <LoadingRing size="large" />
    </div>
);

const AdminPage = () => {
    const context = useContext(AuthContext);
    const canManageActors = context.orRights([RIGHTS_KEYS.manageActors]);
    const canManageEndUsers = context.orRights([RIGHTS_KEYS.manageEndUsers]);
    const canViewActorsTab = canManageActors || canManageEndUsers;
    const canManageImplants = context.orRights([RIGHTS_KEYS.manageAllImplants]);
    const canManageDocSchemas = context.orRights([
        RIGHTS_KEYS.manageDocumentSchemas,
    ]);
    const canManageAllUsers = context.orRights([RIGHTS_KEYS.manageAllUsers]);

    const tabs: TabDef[] = [
        {
            value: 'actors',
            label: 'Actors',
            icon: <Users className="mr-1.5 h-4 w-4" />,
            visible: canViewActorsTab,
        },
        {
            value: 'synonyms',
            label: 'Synonyms',
            icon: <BookType className="mr-1.5 h-4 w-4" />,
            visible: canManageImplants,
        },
        {
            value: 'mri-machines',
            label: 'MRI Machines',
            icon: <Radio className="mr-1.5 h-4 w-4" />,
            visible: canManageImplants,
        },
        {
            value: 'ai-tasks',
            label: 'AI Tasks',
            icon: <Bot className="mr-1.5 h-4 w-4" />,
            visible: canManageImplants,
        },
        {
            value: 'metrics',
            label: 'Admin Metrics',
            icon: <BarChart3 className="mr-1.5 h-4 w-4" />,
            visible: canManageAllUsers,
        },
        {
            value: 'document-schemas',
            label: 'Document Schemas',
            icon: <FileText className="mr-1.5 h-4 w-4" />,
            visible: canManageDocSchemas,
        },
        {
            value: 'policy-templates',
            label: 'Policy Templates',
            icon: <FileStack className="mr-1.5 h-4 w-4" />,
            visible: canManageDocSchemas,
        },
        {
            value: 'feature-flags',
            label: 'Beta Features',
            icon: <ToggleLeft className="mr-1.5 h-4 w-4" />,
            visible: canManageAllUsers || canManageEndUsers,
        },
        {
            value: 'schema-demo',
            label: 'Schema Demo',
            icon: <FlaskConical className="mr-1.5 h-4 w-4" />,
            visible: canManageAllUsers,
        },
    ];

    const visibleTabs = tabs.filter((t) => t.visible);
    const defaultTab = visibleTabs[0]?.value ?? 'actors';
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = visibleTabs.some((t) => t.value === tabParam)
        ? (tabParam as string)
        : defaultTab;
    const setActiveTab = (value: string) => {
        setSearchParams({ tab: value }, { replace: true });
    };

    return (
        <div className="container mx-auto max-w-6xl px-4 py-8">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">Administration</h1>
                <p className="text-sm text-muted-foreground">
                    Manage actors, synonyms, MRI machines, and more
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                    <TabsList className="inline-flex min-w-max">
                        {visibleTabs.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value}>
                                {tab.icon}
                                {tab.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                <div className="mt-4 border-t border-subtle" />

                {canViewActorsTab && (
                    <TabsContent value="actors" className="mt-6">
                        <ManageActorsTab
                            scopeRestriction={canManageActors ? undefined : 'department'}
                        />
                    </TabsContent>
                )}
                {canManageImplants && (
                    <TabsContent value="synonyms" className="mt-6">
                        <ManageSynonyms />
                    </TabsContent>
                )}
                {canManageImplants && (
                    <TabsContent value="mri-machines" className="mt-6">
                        <PublicMRIMachines />
                    </TabsContent>
                )}
                {canManageImplants && (
                    <TabsContent value="ai-tasks" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <AIAdminPage />
                        </Suspense>
                    </TabsContent>
                )}
                {canManageAllUsers && (
                    <TabsContent value="metrics" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <AdminMetricsPage />
                        </Suspense>
                    </TabsContent>
                )}
                {canManageDocSchemas && (
                    <TabsContent value="document-schemas" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <ManageDocumentSchemasPage />
                        </Suspense>
                    </TabsContent>
                )}
                {canManageDocSchemas && (
                    <TabsContent value="policy-templates" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <PolicyTemplateListPage />
                        </Suspense>
                    </TabsContent>
                )}
                {(canManageAllUsers || canManageEndUsers) && (
                    <TabsContent value="feature-flags" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <FeatureFlagsTab />
                        </Suspense>
                    </TabsContent>
                )}
                {canManageAllUsers && (
                    <TabsContent value="schema-demo" className="mt-6">
                        <Suspense fallback={<Loading />}>
                            <ReportingWorkspacePage />
                        </Suspense>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
};

export default AdminPage;
